/**
 * Implantação, planos (mensal/anual), modelo de mensalidade e comparativo
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Minus,
  ArrowRight,
  Rocket,
  CalendarCheck,
  Wallet,
  BadgePercent,
  Unlock,
  HeartHandshake,
  Loader2,
} from 'lucide-react';
import { formatBRL, SubscriptionPlan } from '@services/platformService';
import { ONBOARDING_STEPS, COMPARISON_ROWS, BRAND } from '../content';
import { Reveal, SectionHeading } from './Reveal';

export function Onboarding() {
  return (
    <section id="implantacao" className="relative scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Implantação gratuita"
          title={
            <>
              Do cadastro ao primeiro pedido <span className="vw-gradient-text">no mesmo dia</span>
            </>
          }
          description="Sem instalação, sem consultor obrigatório e sem taxa de implantação. Você mesmo configura em poucos passos, e nosso time ajuda se precisar."
        />
        <ol className="relative mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div
            className="absolute left-0 right-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent lg:block"
            aria-hidden
          />
          {ONBOARDING_STEPS.map((step, i) => (
            <Reveal as="li" key={step.title} delay={i * 100} className="relative">
              <div className="flex items-center gap-3">
                <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-xl font-bold text-white shadow-lg shadow-violet-900/50">
                  {i + 1}
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
                  {step.time}
                </span>
              </div>
              <h3 className="mt-5 text-lg font-semibold text-white">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.description}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function PlanPrice({ plan, yearly }: { plan: SubscriptionPlan; yearly: boolean }) {
  if (plan.isTrial || plan.priceMonthly === 0) {
    return (
      <div>
        <p className="text-4xl font-extrabold tracking-tight text-white">Grátis</p>
        <p className="mt-1 text-sm text-slate-400">por {plan.trialDays} dias, sem cartão</p>
      </div>
    );
  }
  const hasYearly = yearly && plan.priceYearly !== null && plan.priceYearly > 0;
  const monthly = hasYearly ? (plan.priceYearly as number) / 12 : plan.priceMonthly;
  const discount = hasYearly
    ? Math.round((1 - (plan.priceYearly as number) / (plan.priceMonthly * 12)) * 100)
    : 0;
  return (
    <div>
      {hasYearly && discount > 0 && (
        <p className="text-sm text-slate-400 line-through">{formatBRL(plan.priceMonthly)}/mês</p>
      )}
      <p className="flex items-baseline gap-1">
        <span className="text-4xl font-extrabold tracking-tight text-white">
          {formatBRL(monthly)}
        </span>
        <span className="text-sm text-slate-400">/mês</span>
      </p>
      <p className="mt-1 text-sm text-slate-400">
        {hasYearly ? (
          <>
            {formatBRL(plan.priceYearly as number)} por ano
            {discount > 0 && (
              <span className="ml-1 font-semibold text-emerald-300">({discount}% off)</span>
            )}
          </>
        ) : (
          <>
            cobrança mensal ·{' '}
            {plan.trialDays > 0 ? `${plan.trialDays} dias grátis` : 'sem fidelidade'}
          </>
        )}
      </p>
    </div>
  );
}

export function Pricing({
  plans,
  loading,
  onTalkToSales,
  segmentType,
}: {
  plans: SubscriptionPlan[];
  loading: boolean;
  onTalkToSales: (planSlug: string) => void;
  /** Segmento pré-selecionado no cadastro (páginas /para/:segmento) */
  segmentType?: string;
}) {
  const [yearly, setYearly] = useState(false);
  const hasYearlyOption = plans.some(p => p.priceYearly !== null && p.priceYearly > 0);
  const gridCols =
    plans.length === 4
      ? 'lg:grid-cols-4'
      : plans.length === 2
        ? 'lg:grid-cols-2'
        : 'lg:grid-cols-3';

  return (
    <section id="planos" className="relative scroll-mt-20 py-20 sm:py-28">
      <div
        className="vw-orb -z-10 left-[5%] top-[30%] h-[380px] w-[380px] bg-fuchsia-800/40"
        aria-hidden
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Planos e preços"
          title={
            <>
              Preço justo e transparente. <span className="vw-gradient-text">Zero comissão.</span>
            </>
          }
          description="Todos os planos começam com teste grátis, têm pedidos ilimitados e podem ser cancelados quando você quiser."
        />

        {hasYearlyOption && (
          <Reveal className="mt-10 flex justify-center">
            <div
              className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] p-1"
              role="group"
              aria-label="Período de cobrança"
            >
              {[
                { value: false, label: 'Mensal' },
                { value: true, label: 'Anual' },
              ].map(opt => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setYearly(opt.value)}
                  aria-pressed={yearly === opt.value}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                    yearly === opt.value
                      ? 'bg-white text-slate-900'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {opt.label}
                  {opt.value && <span className="ml-1.5 text-xs text-emerald-500">economize</span>}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        {loading ? (
          <div className="mt-16 flex justify-center text-violet-300">
            <Loader2 className="animate-spin" size={32} aria-label="Carregando planos" />
          </div>
        ) : (
          <div className={`mt-12 grid gap-6 md:grid-cols-2 ${gridCols}`}>
            {plans.map((plan, i) => {
              const featured = plan.highlight;
              const params = new URLSearchParams();
              if (!plan.isTrial) params.set('plano', plan.slug);
              if (segmentType) params.set('segmento', segmentType);
              const query = params.toString();
              const signupUrl = query ? `/cadastro?${query}` : '/cadastro';
              return (
                <Reveal
                  key={plan.id}
                  delay={i * 80}
                  className={`relative flex flex-col rounded-3xl p-7 ${
                    featured
                      ? 'vw-gradient-border bg-gradient-to-b from-violet-900/70 to-slate-950/90 shadow-2xl shadow-violet-900/40 lg:-mt-4 lg:mb-[-1rem]'
                      : 'border border-white/10 bg-white/[0.03]'
                  }`}
                >
                  {plan.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-1 text-xs font-bold text-white shadow-lg">
                      {plan.badge}
                    </span>
                  )}
                  <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                  {plan.description && (
                    <p className="mt-2 min-h-[3rem] text-sm leading-relaxed text-slate-400">
                      {plan.description}
                    </p>
                  )}
                  <div className="mt-6">
                    <PlanPrice plan={plan} yearly={yearly} />
                  </div>
                  {plan.setupFee > 0 ? (
                    <p className="mt-2 text-xs text-slate-400">
                      + implantação de {formatBRL(plan.setupFee)}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs font-medium text-emerald-300">
                      Implantação gratuita
                    </p>
                  )}
                  <Link
                    to={signupUrl}
                    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                      featured
                        ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-900/40 hover:brightness-110'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    }`}
                  >
                    {plan.isTrial
                      ? 'Começar grátis'
                      : `Testar ${plan.trialDays > 0 ? `${plan.trialDays} dias ` : ''}grátis`}
                    <ArrowRight size={16} />
                  </Link>
                  {!plan.isTrial && (
                    <button
                      type="button"
                      onClick={() => onTalkToSales(plan.slug)}
                      className="mt-2 rounded-xl px-5 py-2 text-sm font-medium text-slate-300 hover:text-white"
                    >
                      Falar com um especialista
                    </button>
                  )}
                  <ul className="mt-6 space-y-3 border-t border-white/10 pt-6">
                    {plan.features.map(feature => (
                      <li key={feature} className="flex gap-2.5 text-sm text-slate-300">
                        <Check size={16} className="mt-0.5 shrink-0 text-violet-300" /> {feature}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

export function BillingModel({ trialDays }: { trialDays: number }) {
  const items = [
    {
      icon: Rocket,
      title: `${trialDays} dias grátis`,
      text: 'Use tudo sem cartão de crédito. Sem cobrança automática ao final do teste.',
    },
    {
      icon: CalendarCheck,
      title: 'Mensalidade fixa',
      text: 'Você escolhe o plano e paga um valor fixo por mês, com pedidos ilimitados.',
    },
    {
      icon: BadgePercent,
      title: 'Zero comissão',
      text: `O ${BRAND} não fica com nenhum percentual das suas vendas, nunca.`,
    },
    {
      icon: Wallet,
      title: 'Dinheiro direto para você',
      text: 'Pagamentos online caem na sua conta Mercado Pago. Só incide a taxa da operadora.',
    },
    {
      icon: Unlock,
      title: 'Sem fidelidade',
      text: 'Cancele quando quiser. No plano anual você ganha desconto por antecipar.',
    },
    {
      icon: HeartHandshake,
      title: 'Suporte humano',
      text: 'Ajuda na configuração da loja e no dia a dia pelo WhatsApp e e-mail.',
    },
  ];
  return (
    <section className="relative py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Como funciona a mensalidade"
          title="Sem letras miúdas"
          description="Um modelo simples para você saber exatamente quanto vai pagar, todo mês."
        />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <Reveal
              key={item.title}
              delay={i * 60}
              className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-300">
                <item.icon size={20} />
              </span>
              <div>
                <h3 className="font-semibold text-white">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cell({ value, highlight = false }: { value: string | boolean; highlight?: boolean }) {
  if (value === true) {
    return (
      <span
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${highlight ? 'bg-emerald-400/20 text-emerald-300' : 'bg-white/5 text-slate-300'}`}
      >
        <Check size={16} aria-label="Sim" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/5 text-slate-400">
        <Minus size={16} aria-label="Não" />
      </span>
    );
  }
  return <span className={highlight ? 'font-semibold text-white' : 'text-slate-400'}>{value}</span>;
}

export function Comparison() {
  return (
    <section className="relative py-20 sm:py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Comparativo"
          title={
            <>
              Por que escolher o <span className="vw-gradient-text">{BRAND}</span>
            </>
          }
        />
        <Reveal className="mt-12 overflow-x-auto rounded-3xl border border-white/10">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.03]">
                <th scope="col" className="px-5 py-4 font-medium text-slate-400">
                  Recurso
                </th>
                <th
                  scope="col"
                  className="bg-violet-500/10 px-5 py-4 text-center font-bold text-white"
                >
                  {BRAND}
                </th>
                <th scope="col" className="px-5 py-4 text-center font-medium text-slate-400">
                  Marketplaces
                </th>
                <th scope="col" className="px-5 py-4 text-center font-medium text-slate-400">
                  Outros sistemas
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {COMPARISON_ROWS.map(row => (
                <tr key={row.label}>
                  <th scope="row" className="px-5 py-4 font-medium text-slate-300">
                    {row.label}
                  </th>
                  <td className="bg-violet-500/[0.06] px-5 py-4 text-center">
                    <Cell value={row.vendeai} highlight />
                  </td>
                  <td className="px-5 py-4 text-center">
                    <Cell value={row.marketplace} />
                  </td>
                  <td className="px-5 py-4 text-center">
                    <Cell value={row.others} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
        <p className="mt-4 text-center text-xs text-slate-400">
          Informações de mercado baseadas em planos públicos de plataformas de delivery e cardápio
          digital (set/2026).
        </p>
      </div>
    </section>
  );
}
